import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { PayrollPeriodService } from 'src/app/services/payroll-period.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

declare const $: any;

export interface CierreNominaPeriod {
    id: number | string;
    period_date?: string;
}

export interface CierreNominaEvent {
    id: number | string;
    response: any;
}

/**
 * Modal de 2 pasos "Cierre total de nómina", calcado del shell de
 * `termination-modal`. Paso 1 confirma la intención sobre el periodo
 * específico; paso 2 exige re-autenticación con la clave del usuario
 * (Hash::check en backend) como último gate antes de ejecutar el cierre.
 */
@Component({
    selector: 'app-cierre-nomina-modal',
    templateUrl: './cierre-nomina-modal.component.html',
    styleUrls: ['./cierre-nomina-modal.component.css']
})
export class CierreNominaModalComponent {
    @Input() modalId = 'cierreNominaModal';

    /** Emitido cuando el periodo se cerró exitosamente en el backend. */
    @Output() closed = new EventEmitter<CierreNominaEvent>();

    step: 1 | 2 = 1;
    period: CierreNominaPeriod | null = null;

    password = '';
    passwordError = '';
    submitting = false;

    constructor(
        private payrollPeriodService: PayrollPeriodService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Administrador de periodos', action);
    }

    get periodLabel(): string {
        if (!this.period?.period_date) return '';
        const datePart = String(this.period.period_date).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '';
        return `${dd}/${mm}/${yyyy}`;
    }

    /** Abre el modal en el paso 1 para el periodo indicado. */
    open(period: CierreNominaPeriod): void {
        this.period = period;
        this.step = 1;
        this.password = '';
        this.passwordError = '';
        this.submitting = false;
        $(`#${this.modalId}`).modal('show');
    }

    close(): void {
        $(`#${this.modalId}`).modal('hide');
    }

    cancel(): void {
        this.close();
    }

    /**
     * Paso 2 ("Realizar cierre total"): verifica la clave del usuario y,
     * solo si es correcta, ejecuta el cierre del periodo.
     */
    verifyAndClose(): void {
        if (!this.period) return;

        if (!this.password) {
            this.passwordError = 'La clave de usuario es obligatoria';
            return;
        }

        this.passwordError = '';
        this.submitting = true;
        this.spinner.show();
        this.payrollPeriodService.verifyPassword(this.period.id, this.password).subscribe({
            next: () => this.executeClose(),
            error: (err: any) => {
                this.submitting = false;
                this.spinner.hide();
                // PayrollPeriodService pasa por ApiService, que ya reduce el
                // error HTTP a un string plano — err no trae err.error.message.
                this.passwordError = (typeof err === 'string' && err) || 'Clave de usuario incorrecta';
            }
        });
    }

    private executeClose(): void {
        this.payrollPeriodService.close(this.period!.id).subscribe({
            next: (resp: any) => {
                this.submitting = false;
                this.spinner.hide();
                this.close();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'Se realizó el cierre total de nómina correctamente', icon: 'success' });
                this.closed.emit({ id: this.period!.id, response: resp });
            },
            error: (err: any) => {
                this.submitting = false;
                this.spinner.hide();
                const text = (typeof err === 'string' && err) || 'No se pudo cerrar el periodo de nómina';
                this.utils.alertSwal({ title: 'Error', text, icon: 'error' });
            }
        });
    }
}
